/**
 * The safety net under the structured panes.
 *
 * The funding-statement endpoints pin a specific set of field names
 * (`app/models/funding_statements.py`) and the panes render those deliberately.
 * Everything ELSE in a response — a field the backend adds later, a
 * product-conditional key that only appears on TFS/DSCR — falls through to
 * `AdaptiveRows` inside a collapsed `Disclosure`, so a new backend field shows up
 * on the tab automatically instead of being silently dropped.
 */
import { useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { EMPTY, T, labelSx, money, ratePct } from "./wireFundingShared";

/** camelCase / snake_case key → "Title Case" label. */
export const humanizeKey = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const PERCENT_KEY = /(pct|percent|rate|ltv|dscr)/i;
const DATE_KEY = /(date|at)$/i;

// Numbers are dollars unless the key reads like a rate — we can't know units
// for certain from an untyped payload, so this is a labeled best effort.
const formatValue = (key, value) => {
  if (value === null || value === undefined || value === "") return EMPTY;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    return PERCENT_KEY.test(String(key)) ? ratePct(value) : money(value);
  }
  if (
    typeof value === "string" &&
    DATE_KEY.test(String(key)) &&
    /^\d{4}-\d{2}-\d{2}/.test(value)
  ) {
    return formatDate(value, value, "MMM d, yyyy");
  }
  return String(value);
};

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * Walks whatever object it is handed: scalars become a labeled row, nested
 * objects recurse under a sub-heading, arrays render each item. Lays rows out
 * in two columns at width — these are reference details, not a ledger.
 */
export const AdaptiveRows = ({ data, depth = 0 }) => {
  if (data === null || data === undefined) return null;

  if (Array.isArray(data)) {
    if (data.length === 0) return null;
    return (
      <Flx column gap={0.5} fw>
        {data.map((item, i) =>
          isPlainObject(item) ? (
            <Box
              key={i}
              sx={{
                pl: depth ? 1.25 : 0,
                borderLeft: depth ? `2px solid ${T.line}` : undefined,
              }}
            >
              <AdaptiveRows data={item} depth={depth + 1} />
            </Box>
          ) : (
            <Txt key={i} sx={{ fontSize: 12, color: T.ink2 }}>
              {formatValue("", item)}
            </Txt>
          ),
        )}
      </Flx>
    );
  }

  if (isPlainObject(data)) {
    const entries = Object.entries(data);
    if (entries.length === 0) return null;
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: depth ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
          },
          columnGap: 4,
        }}
      >
        {entries.map(([key, value]) => {
          if (isPlainObject(value) || Array.isArray(value)) {
            const hasContent = isPlainObject(value)
              ? Object.keys(value).length > 0
              : value.length > 0;
            if (!hasContent) return null;
            return (
              <Box key={key} sx={{ mt: 0.75, gridColumn: "1 / -1" }}>
                <Txt sx={{ ...labelSx, display: "block", mb: 0.4 }}>
                  {humanizeKey(key)}
                </Txt>
                <Box sx={{ pl: 1.25, borderLeft: `2px solid ${T.line}` }}>
                  <AdaptiveRows data={value} depth={depth + 1} />
                </Box>
              </Box>
            );
          }
          return (
            <Flx
              key={key}
              jb
              ac
              gap={1.5}
              fw
              sx={{ py: 0.4, borderBottom: `1px dashed ${T.line2}` }}
            >
              <Txt sx={{ fontSize: 11.5, color: T.muted }}>
                {humanizeKey(key)}
              </Txt>
              <Txt
                sx={{
                  fontSize: 11.5,
                  color: T.ink2,
                  textAlign: "right",
                  fontFamily: typeof value === "number" ? T.mono : undefined,
                }}
              >
                {formatValue(key, value)}
              </Txt>
            </Flx>
          );
        })}
      </Box>
    );
  }

  return <Txt sx={{ fontSize: 12, color: T.ink2 }}>{formatValue("", data)}</Txt>;
};

AdaptiveRows.propTypes = { data: PropTypes.any, depth: PropTypes.number };

/** Collapsed-by-default section. */
export const Disclosure = ({ label, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ borderTop: `1px solid ${T.line}`, mt: 2 }}>
      <Flx
        ac
        gap={0.5}
        onClick={() => setOpen((v) => !v)}
        sx={{ cursor: "pointer", py: 1.25, userSelect: "none" }}
      >
        <ExpandMoreRounded
          sx={{
            fontSize: 16,
            color: T.faint,
            transform: open ? "none" : "rotate(-90deg)",
            transition: "transform .15s",
          }}
        />
        <Txt sx={{ ...labelSx, color: T.muted }}>{label}</Txt>
      </Flx>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pb: 2 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

Disclosure.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
};

/** Drop the keys a pane renders itself; return null when nothing is left over. */
export const leftoverKeys = (payload, renderedKeys) => {
  if (!payload || typeof payload !== "object") return null;
  const out = {};
  Object.entries(payload).forEach(([k, v]) => {
    if (!renderedKeys.has(k)) out[k] = v;
  });
  return Object.keys(out).length ? out : null;
};
